namespace e5 {
    export interface IPoint3dValue extends IPoint {
        z: number;
        w?: number;
    }

    export class Point3d implements IPoint3dValue {

        public static X_AXIS: Point3d = new Point3d(1, 0, 0);
        public static Y_AXIS: Point3d = new Point3d(0, 1, 0);
        public static Z_AXIS: Point3d = new Point3d(0, 0, 1);

        constructor(
            public x: number = 0,
            public y: number = 0,
            public z: number = 0,
            public w: number = 1) {
        }

        public static create(
            x: number = 0,
            y: number = 0,
            z: number = 0,
            w: number = 1): Point3d {
            return new Point3d(x, y, z, w);
        }

        public getData(): number[] {
            return [this.x, this.y, this.z];
        }

        public setData(data: number[], offset: number = 0): Point3d {
            if (data === undefined)
                return this;
            this.x = data[0 + offset];
            this.y = data[1 + offset];
            this.z = data[2 + offset];
            this.w = data.length > (3 + offset) ? data[3 + offset] : 1;
            return this;
        }

        public copyFrom(pt: IPoint3dValue): Point3d {
            this.x = pt.x;
            this.y = pt.y;
            this.z = pt.z;
            this.w = pt.w;
            return this;
        }

        public copyFrom2d(pt: IPoint): Point3d {
            this.x = pt.x;
            this.y = pt.y;
            this.z = 0;
            this.w = 1;
            return this;
        }

        public copyTo(target: IPoint3dValue): Point3d {
            target.x = this.x;
            target.y = this.y;
            target.z = this.z;
            target.w = this.w;
            return this;
        }

        public equals(point: IPoint3dValue): boolean {
            return point.x == this.x && point.y == this.y && point.z == this.z;
        }

        public length(): number {
            return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
        }

        public normalize(length: number = 1): Point3d {
            var len: number = this.length();
            if (!len)
                return this;
            this.scalar(length / len);
            return this;
        }

        public clamp(length: number): Point3d {
            var len: number = this.length();
            if (!len || len <= length)
                return this;
            this.scalar(length / len);
            return this;
        }

        public scalar(scale: number): Point3d {
            this.x *= scale;
            this.y *= scale;
            this.z *= scale;
            return this;
        }

        public setTo(x: number, y: number, z: number, w: number = 1): Point3d {
            this.x = x;
            this.y = y;
            this.z = z;
            this.w = w;
            return this;
        }

        public cross(pt: IPoint3dValue): Point3d {
            var x = this.x;
            var y = this.y;
            var z = this.z;
            this.x = y * pt.z - z * pt.y;
            this.y = z * pt.x - x * pt.z;
            this.z = x * pt.y - y * pt.x;
            return this;
        }

        public crossSet(a: IPoint3dValue, b: IPoint3dValue): Point3d {
            var ax = a.x;
            var ay = a.y;
            var az = a.z;
            var bx = b.x;
            var by = b.y;
            var bz = b.z;
            this.x = ay * bz - az * by;
            this.y = az * bx - ax * bz;
            this.z = ax * by - ay * bx;
            return this;
        }

        public mul(pt: IPoint3dValue): Point3d {
            this.x *= pt.x;
            this.y *= pt.y;
            this.z *= pt.z;
            return this;
        }

        public product(factorA: IPoint3dValue, factorB: IPoint3dValue): Point3d {
            this.x = factorA.x * factorB.x;
            this.y = factorA.y * factorB.y;
            this.z = factorA.z * factorB.z;
            return this;
        }

        public div(divisor: IPoint3dValue): Point3d {
            this.x /= divisor.x;
            this.y /= divisor.y;
            this.z /= divisor.z;
            return this;
        }

        public quotient(divident: IPoint3dValue, divisor: IPoint3dValue): Point3d {
            this.x = divident.x / divisor.x;
            this.y = divident.y / divisor.y;
            this.z = divident.z / divisor.z;
            return this;
        }

        public dot(pt: IPoint3dValue): number {
            return this.x * pt.x + this.y * pt.y + this.z * pt.z;
        }

        public add(summand: IPoint3dValue): Point3d {
            this.x += summand.x;
            this.y += summand.y;
            this.z += summand.z;
            return this;
        }

        public translate(x: number, y: number, z: number): Point3d {
            this.x += x;
            this.y += y;
            this.z += z;
            return this;
        }

        public sum(summandA: IPoint3dValue, summandB: IPoint3dValue): Point3d {
            this.x = summandA.x + summandB.x;
            this.y = summandA.y + summandB.y;
            this.z = summandA.z + summandB.z;
            return this;
        }

        public sub(subtrahend: IPoint3dValue): Point3d {
            this.x -= subtrahend.x;
            this.y -= subtrahend.y;
            this.z -= subtrahend.z;
            return this;
        }

        public difference(minuend: IPoint3dValue, subtrahend: IPoint3dValue): Point3d {
            this.x = minuend.x - subtrahend.x;
            this.y = minuend.y - subtrahend.y;
            this.z = minuend.z - subtrahend.z;
            return this;
        }

        public clone(): Point3d {
            return new Point3d(this.x, this.y, this.z, this.w);
        }

        public static interpolate(v0: IPoint3dValue, v1: IPoint3dValue, f: number): Point3d {
            return new Point3d(
                v0.x + (v1.x - v0.x) * f,
                v0.y + (v1.y - v0.y) * f,
                v0.z + (v1.z - v0.z) * f);
        }

        //TODO: implement it
        public static polar(angle: IPoint3dValue, length: number, ret: Point3d = new Point3d()): Point3d {
            var angRad = angle.x * Math.PI / 180;
            ret.x = Math.cos(angRad) * length;
            ret.y = Math.sin(angRad) * length;
            return ret;
        }

        public static distance(v0: IPoint3dValue, v1: IPoint3dValue): number {
            var dx: number = v0.x - v1.x;
            var dy: number = v0.y - v1.y;
            var dz: number = v0.z - v1.z;
            return Math.sqrt(dx * dx + dy * dy + dz * dz);
        }

        public static reflect(vector: IPoint3dValue, normal: IPoint3dValue): Point3d {
            var dp: number = vector.x * normal.x + vector.y * normal.y + vector.z * normal.z;
            return new Point3d(vector.x - 2 * dp * normal.x, vector.y - 2 * dp * normal.y, vector.z - 2 * dp * normal.z);
        }

        public static magnitude(point:IPoint3dValue): number {
            return Math.sqrt(point.x * point.x + point.y * point.y + point.z * point.z);
        }
    }
}